/**
 * DCTA-191 (docs/specs/fe-audio-evidence-dcta191.md Change 1; Q5 "raw audio", ADR-0024) — the listening session's PCM,
 * kept so the bank can keep it as the turn's evidence. The same 1,280-byte chunks of 16 kHz 16-bit mono PCM the Tencent
 * engine streams (adapters/tencent-asr.ts), in order, across the engine restarts of one listening, capped at 60 s; then
 * wrapped in the 44-byte RIFF header the bank's strict parser takes (lab/dcta src/audio/wav.ts — `wavOf` below is its
 * copy, byte for byte). Voice carries no authority: nothing reads or decides on these bytes; they are the record.
 */
const SAMPLE_RATE = 16_000;
const BYTES_PER_SECOND = SAMPLE_RATE * 2;
const HEADER_BYTES = 44;
/** The bank's cap (WAV_MAX_SECONDS = 60, the ASR session's own): past it nothing more is kept. */
export const PCM_CAP_BYTES = 60 * BYTES_PER_SECOND;
/** Shorter than this is a tap, not words (the bank's WAV_MIN_MS): not sent. */
export const MIN_CLIP_MS = 200;

export class PcmClip {
  #chunks: Uint8Array[] = [];
  #bytes = 0;

  get bytes(): number {
    return this.#bytes;
  }

  /** One chunk as the microphone gave it; past the cap, the part that fits (an even number of bytes), then nothing. */
  append(pcm: ArrayBuffer): void {
    const room = PCM_CAP_BYTES - this.#bytes;
    if (room <= 0) return;
    const bytes = new Uint8Array(pcm);
    const take = bytes.length <= room ? bytes : bytes.subarray(0, room - (room % 2));
    if (take.length === 0) return;
    this.#chunks.push(take);
    this.#bytes += take.length;
  }

  /** Drop what was kept — Cancel, a new listening, words that go another way. */
  clear(): void {
    this.#chunks = [];
    this.#bytes = 0;
  }

  /** The kept PCM as one run, and the clip empty again; nothing when it is shorter than 0.2 s. */
  take(): Uint8Array | undefined {
    const bytes = this.#bytes;
    const chunks = this.#chunks;
    this.clear();
    if ((bytes * 1000) / BYTES_PER_SECOND < MIN_CLIP_MS) return undefined;
    const out = new Uint8Array(bytes);
    let at = 0;
    for (const c of chunks) {
      out.set(c, at);
      at += c.length;
    }
    return out;
  }
}

/** 16 kHz mono s16le PCM → the 44-byte RIFF header + the samples (lab/dcta src/audio/wav.ts `wavOf`, the same bytes). */
export function wavOf(pcm: Uint8Array): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(HEADER_BYTES + pcm.length);
  const view = new DataView(out.buffer);
  out.set([0x52, 0x49, 0x46, 0x46], 0); // RIFF
  view.setUint32(4, out.length - 8, true);
  out.set([0x57, 0x41, 0x56, 0x45], 8); // WAVE
  out.set([0x66, 0x6d, 0x74, 0x20], 12); // "fmt "
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, SAMPLE_RATE, true);
  view.setUint32(28, BYTES_PER_SECOND, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  out.set([0x64, 0x61, 0x74, 0x61], 36); // data
  view.setUint32(40, pcm.length, true);
  out.set(pcm, HEADER_BYTES);
  return out;
}
